import {
  DEFAULT_COHORT,
  DEFAULT_EXCLUSION_POPULATION,
  DEFAULT_PARAMS,
  DEFAULT_SUBPOPULATION,
} from './defaults';
import type {
  ComputedCohort,
  ComputedSubpopulation,
  ConservationError,
  DisplayCohort,
  DisplayNodes,
  DisplaySubpopulation,
  ExclusionPopulation,
  FollowupCohort,
  FunnelParams,
  FunnelParamsPatch,
  FunnelResult,
  ScreeningOutcomes,
  StoredSubpopulation,
  StoredExclusionPopulation,
  Subpopulation,
} from './types';

/* ------------------------------------------------------------------ maths */

export function clamp(value: unknown, min: number, max: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function nonNegative(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/** Normalize a set of shares to 1. An all-zero set falls back to an even split. */
export function normalizeShares(values: readonly number[]): number[] {
  const safe = values.map((v) => Math.max(0, Number.isFinite(v) ? v : 0));
  const total = sum(safe);
  if (total <= 0) return safe.map(() => (safe.length ? 1 / safe.length : 0));
  return safe.map((v) => v / total);
}

/**
 * Largest-remainder apportionment: round `values` to integers that still sum to
 * `total`, giving leftover units to the biggest fractional parts.
 */
export function largestRemainder(values: readonly number[], total: number): number[] {
  const floors = values.map((v) => Math.floor(Math.max(0, v)));
  let left = Math.round(total) - sum(floors);
  if (left <= 0 || floors.length === 0) return floors;

  const order = values
    .map((v, i) => ({ i, frac: Math.max(0, v) - Math.floor(Math.max(0, v)) }))
    .sort((a, b) => b.frac - a.frac);

  for (let k = 0; left > 0; k++, left--) {
    const slot = order[k % order.length];
    if (!slot) break;
    floors[slot.i] = (floors[slot.i] ?? 0) + 1;
  }
  return floors;
}

/* ------------------------------------------------------------------ merge */

/** Drop undefined values so a sparse patch never clobbers a sibling with undefined. */
function definedOnly<T extends object>(patch: Partial<T> | undefined): Partial<T> {
  if (!patch) return {};
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) out[key as keyof T] = value as T[keyof T];
  }
  return out;
}

function normalizeSubpopulation(row: StoredSubpopulation, index: number): Subpopulation {
  // `conversion` is the pre-split name for `reach`; honor it so a stored
  // scenario keeps the user's own rate instead of silently taking the default.
  const reach = row.reach ?? row.conversion;
  return {
    id: row.id ?? `sub-${index}`,
    name: row.name ?? DEFAULT_SUBPOPULATION.name,
    count: nonNegative(row.count, DEFAULT_SUBPOPULATION.count),
    reach: reach === undefined ? DEFAULT_SUBPOPULATION.reach : clamp(reach, 0, 1),
    screenRate: row.screenRate === undefined
      ? DEFAULT_SUBPOPULATION.screenRate
      : clamp(row.screenRate, 0, 1),
  };
}

function normalizeExclusionPopulation(
  row: StoredExclusionPopulation,
  index: number,
): ExclusionPopulation {
  return {
    id: row.id ?? `exclusion-${index}`,
    name: row.name ?? DEFAULT_EXCLUSION_POPULATION.name,
    count: nonNegative(row.count, DEFAULT_EXCLUSION_POPULATION.count),
  };
}

function normalizeCohort(row: Partial<FollowupCohort>, index: number): FollowupCohort {
  return {
    id: row.id ?? `cohort-${index}`,
    name: row.name ?? DEFAULT_COHORT.name,
    share: row.share === undefined ? DEFAULT_COHORT.share : Math.max(0, Number(row.share) || 0),
    reach: row.reach === undefined ? DEFAULT_COHORT.reach : clamp(row.reach, 0, 1),
    completion: row.completion === undefined
      ? DEFAULT_COHORT.completion
      : clamp(row.completion, 0, 1),
  };
}

/**
 * Merge a partial override onto a base. Nested objects merge key by key; arrays
 * replace wholesale, with each row filled out from the per-row defaults.
 *
 * This is the contract the persistence layer relies on: a backend that stores
 * only changed keys must be able to send just those keys without silently
 * zeroing the siblings it did not mention.
 */
export function mergeParams(
  base: FunnelParams = DEFAULT_PARAMS,
  patch?: FunnelParamsPatch | null,
): FunnelParams {
  if (!patch) return structuredClone(base);
  return {
    subpopulations: patch.subpopulations
      ? patch.subpopulations.map(normalizeSubpopulation)
      : structuredClone(base.subpopulations),
    exclusionPopulations: patch.exclusionPopulations
      ? patch.exclusionPopulations.map(normalizeExclusionPopulation)
      : structuredClone(base.exclusionPopulations),
    outcomes: { ...base.outcomes, ...definedOnly<ScreeningOutcomes>(patch.outcomes) },
    followupCohorts: patch.followupCohorts
      ? patch.followupCohorts.map(normalizeCohort)
      : structuredClone(base.followupCohorts),
  };
}

/* ---------------------------------------------------------------- compute */

export function computeFunnel(input?: FunnelParamsPatch | null): FunnelResult {
  const params = mergeParams(DEFAULT_PARAMS, input);

  /* --- column 1: exclusions leave before outreach or denominator maths --- */
  const sourcePopulation = sum(params.subpopulations.map((s) => Math.max(0, s.count)));
  const requestedExclusions = sum(
    params.exclusionPopulations.map((exclusion) => Math.max(0, exclusion.count)),
  );
  const excludedPopulation = Math.min(sourcePopulation, requestedExclusions);
  const population = Math.max(0, sourcePopulation - excludedPopulation);
  const eligibilityRatio = sourcePopulation > 0 ? population / sourcePopulation : 0;

  /* Exclusions are aggregate groups, not source-specific rows. Allocate them
     proportionally so the source mix and its blended rate assumptions remain
     intact while excluded people never enter outreach. */
  const subpopulations: ComputedSubpopulation[] = params.subpopulations.map((s) => {
    const count = Math.max(0, s.count);
    const eligibleCount = count * eligibilityRatio;
    const reach = clamp(s.reach, 0, 1);
    const screenRate = clamp(s.screenRate, 0, 1);
    const reached = eligibleCount * reach;
    return {
      ...s,
      count,
      eligibleCount,
      reach,
      screenRate,
      reached,
      notReached: Math.max(0, eligibleCount - reached),
      screened: reached * screenRate,
      share: 0,
    };
  });

  for (const s of subpopulations) {
    s.share = population > 0 ? s.eligibleCount / population : 0;
  }

  const reachedScreening = sum(subpopulations.map((s) => s.reached));
  const neverReached = Math.max(0, population - reachedScreening);

  /* Both blended rates fall out of the sources; neither is typed in. */
  const screened = sum(subpopulations.map((s) => s.screened));
  const notScreened = Math.max(0, reachedScreening - screened);

  /* --- column 4: screening outcomes, as a two-level tree ---
     Level 1 partitions everyone screened by the PHQ-9 threshold. Level 2 splits
     the under-10 group by whether a PHQ-9 was needed at all. C-SSRS hangs off
     the follow-up group as an overlay and takes nobody out of it. */
  const [noFollowupShare = 0, followupShare = 0] = normalizeShares([
    params.outcomes.noFollowup,
    params.outcomes.followupRequired,
  ]);
  const noFollowupNeeded = screened * noFollowupShare;
  const phq2NegativeShare = clamp(params.outcomes.phq2NegativeShare, 0, 1);
  const phq2Negative = noFollowupNeeded * phq2NegativeShare;
  const phq9UnderTen = Math.max(0, noFollowupNeeded - phq2Negative);
  const followupRequired = screened * followupShare;
  const cssrsNeeded = followupRequired * clamp(params.outcomes.cssrsShare, 0, 1);

  /* --- columns 5 and 6: per-cohort chase --- */
  const cohortShares = normalizeShares(params.followupCohorts.map((c) => c.share));
  const cohorts: ComputedCohort[] = params.followupCohorts.map((c, i) => {
    const pool = followupRequired * (cohortShares[i] ?? 0);
    const reach = clamp(c.reach, 0, 1);
    const completion = clamp(c.completion, 0, 1);
    const reached = pool * reach;
    const completed = reached * completion;
    return {
      ...c,
      share: cohortShares[i] ?? 0,
      pool,
      reach,
      reached,
      notReached: Math.max(0, pool - reached),
      completion,
      completed,
      notCompleted: Math.max(0, reached - completed),
    };
  });

  const reachedFollowup = sum(cohorts.map((c) => c.reached));
  const notReachedFollowup = Math.max(0, followupRequired - reachedFollowup);
  const completed = sum(cohorts.map((c) => c.completed));
  const notCompleted = Math.max(0, reachedFollowup - completed);

  return {
    params,
    subpopulations,
    cohorts,
    population: {
      source: sourcePopulation,
      excluded: excludedPopulation,
      unappliedExclusions: Math.max(0, requestedExclusions - sourcePopulation),
    },
    nodes: {
      population,
      reachedScreening,
      neverReached,
      screened,
      notScreened,
      noFollowupNeeded,
      phq2Negative,
      phq9UnderTen,
      followupRequired,
      cssrsNeeded,
      reachedFollowup,
      notReachedFollowup,
      completed,
      notCompleted,
    },
    rates: {
      screeningReach: population > 0 ? reachedScreening / population : 0,
      screenGivenReached: reachedScreening > 0 ? screened / reachedScreening : 0,
      followupRequiredShare: screened > 0 ? followupRequired / screened : 0,
      followupReach: followupRequired > 0 ? reachedFollowup / followupRequired : 0,
      followupCompletion: reachedFollowup > 0 ? completed / reachedFollowup : 0,
      screeningRate: population > 0 ? screened / population : 0,
      endToEnd: population > 0 ? completed / population : 0,
    },
  };
}

/* ---------------------------------------------------------------- display */

/**
 * Integer labels reconciled at every parent, so the visible children always sum
 * to the visible parent — including inside each follow-up cohort.
 */
export function roundForDisplay(result: FunnelResult): DisplayNodes {
  const n = result.nodes;

  const population = Math.round(n.population);
  const reachedScreening = Math.min(population, Math.round(n.reachedScreening));
  const screened = Math.min(reachedScreening, Math.round(n.screened));

  /* Apportion the per-source columns against their own rounded totals, so each
     source list adds up to the column it feeds. */
  const subCounts = largestRemainder(
    result.subpopulations.map((s) => s.eligibleCount),
    population,
  );
  const subScreened = largestRemainder(result.subpopulations.map((s) => s.screened), screened);
  const subpopulations: DisplaySubpopulation[] = result.subpopulations.map((s, i) => {
    const count = subCounts[i] ?? 0;
    const reached = Math.min(count, Math.round(s.reached));
    return {
      id: s.id,
      name: s.name,
      count,
      reached,
      notReached: count - reached,
      screened: subScreened[i] ?? 0,
    };
  });

  const [phq2Negative = 0, phq9UnderTen = 0, followupRequired = 0] = largestRemainder(
    [n.phq2Negative, n.phq9UnderTen, n.followupRequired],
    screened,
  );
  // Derive the group from its rounded children so the tree cannot disagree.
  const noFollowupNeeded = phq2Negative + phq9UnderTen;
  const cssrsNeeded = Math.min(followupRequired, Math.round(n.cssrsNeeded));

  const pools = largestRemainder(result.cohorts.map((c) => c.pool), followupRequired);
  const cohorts: DisplayCohort[] = result.cohorts.map((c, i) => {
    const pool = pools[i] ?? 0;
    const reached = Math.min(pool, Math.round(c.reached));
    const done = Math.min(reached, Math.round(c.completed));
    return {
      id: c.id,
      name: c.name,
      pool,
      reached,
      notReached: pool - reached,
      completed: done,
      notCompleted: reached - done,
    };
  });

  const reachedFollowup = sum(cohorts.map((c) => c.reached));
  const completed = sum(cohorts.map((c) => c.completed));

  return {
    sourcePopulation: Math.round(result.population.source),
    // Derive this from the two displayed totals so the summary always reconciles.
    excludedPopulation: Math.max(0, Math.round(result.population.source) - population),
    population,
    reachedScreening,
    neverReached: population - reachedScreening,
    screened,
    notScreened: reachedScreening - screened,
    noFollowupNeeded,
    phq2Negative,
    phq9UnderTen,
    followupRequired,
    cssrsNeeded,
    reachedFollowup,
    notReachedFollowup: followupRequired - reachedFollowup,
    completed,
    notCompleted: reachedFollowup - completed,
    subpopulations,
    cohorts,
  };
}

/* ----------------------------------------------------------- conservation */

export function conservationErrors(result: FunnelResult, tolerance = 1e-9): ConservationError[] {
  const n = result.nodes;
  const checks: Array<[string, number, number]> = [
    [
      'population = Σ eligible subpopulations',
      n.population,
      sum(result.subpopulations.map((s) => s.eligibleCount)),
    ],
    [
      'source population splits into eligible + excluded',
      result.population.source,
      n.population + result.population.excluded,
    ],
    ['reached = Σ subpopulation reach', n.reachedScreening, sum(result.subpopulations.map((s) => s.reached))],
    ['screened = Σ subpopulation screens', n.screened, sum(result.subpopulations.map((s) => s.screened))],
    ['population splits', n.population, n.reachedScreening + n.neverReached],
    ['reached splits', n.reachedScreening, n.screened + n.notScreened],
    ['screened splits', n.screened, n.phq2Negative + n.phq9UnderTen + n.followupRequired],
    ['no-follow-up group splits', n.noFollowupNeeded, n.phq2Negative + n.phq9UnderTen],
    ['chase splits', n.followupRequired, n.reachedFollowup + n.notReachedFollowup],
    ['completion splits', n.reachedFollowup, n.completed + n.notCompleted],
  ];

  // The cohorts partition the chase pool, but an empty partition of a non-empty
  // pool is ill-defined. With no cohorts the pool is simply unworked — which the
  // column-level checks above already assert — so only require the partition to
  // add up when there is one.
  if (result.cohorts.length > 0) {
    checks.push([
      'chase pool = Σ cohort pools',
      n.followupRequired,
      sum(result.cohorts.map((c) => c.pool)),
    ]);
  }

  for (const c of result.cohorts) {
    checks.push([`cohort ${c.id} pool`, c.pool, c.reached + c.notReached]);
    checks.push([`cohort ${c.id} reached`, c.reached, c.completed + c.notCompleted]);
  }

  return checks
    .filter(([, expected, actual]) => {
      const scale = Math.max(1, Math.abs(expected), Math.abs(actual));
      return Math.abs(expected - actual) > tolerance * scale;
    })
    .map(([label, expected, actual]) => ({ label, expected, actual }));
}
